import { Component, Input, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { DeviceFactory } from '../../../../device-factory/device-factory';
import { DeviceService } from '../../../../device.service';
import { ModalComponent } from 'src/app/shared/modal/modal.component';
import { CommonService } from 'src/app/shared/services/common.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-usage-percent-chart',
	templateUrl: './usage-percent-chart.component.html',
	styleUrls: ['./usage-percent-chart.component.css']
})
export class UsagePercentChartComponent {
	@ViewChild("popupModal") popupModal: ModalComponent;

	@Input() title: any = {};
	@Input() devices: any = [];
	@Input() data: any = [];
	@Input() toDrawValue: string = 'score';
	@Input() maxValue: number = 100;
	@Input() deviceType: number = DeviceFactory.types.iotah;
	@Input() selectedPeriod: string = 'last_week';

	siteID: number = 0;
	customerID: number = 0;
	selectedRangeTitle: string = '';

	// table variables
	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: true,
		fileName: this.title || '',
		hasPagination: true,
		pageSize: 100,
		csvFileHeaderInfo: []
	};

	columnConfig: ColumnsConfig[] = [
		{ key: 'serial_number', name: this.translateService.instant('g.serial_number'), type: "link"},
		{ key: 'device_id', name: this.deviceService.translate('g.device_id', {sct_device_type: this.deviceType, is_study_device: false}), type: "string" },
		{ key: 'value', name: this.translateService.instant(`g.${this.toDrawValue}`), type: "number"},
	];

	// chart variables
	chartData = [];
	ranges = [];
	columnNames = [this.title, this.translateService.instant('fleet_reports.total_number_of_chargers')];
	config = {
		hAxis: {
			title: this.title,
		},
		vAxis: {
			title: this.translateService.instant('fleet_reports.total_number_of_chargers'),
			format: '#\'%\'',
			ticks: [],
		},
		colors: ['#8bcbe5'],
		legend: 'none',
	}

	siteSub: Subscription = new Subscription();
	siteInfo: any = {};

	constructor(
		private translateService: TranslateService,
		private route: ActivatedRoute,
		private deviceService: DeviceService,
		private commonService: CommonService,
		private sideMenuService: SideMenuService
	) {}

	ngOnInit() {
		this.chartData = [];
		this.ranges = [];
		this.siteID = this.route.parent.snapshot.params.siteId || 0;
		this.customerID = this.route.parent.snapshot.params.customerId || 0;
		this.tableData = [];
		this.tableConfig.fileName = (this.title || '') + `_${this.selectedPeriod}`;
		this.columnNames = [this.title, this.translateService.instant('fleet_reports.total_number_of_chargers')];
		this.config.hAxis.title = this.title || this.translateService.instant('site.utilization_score');

		this.siteSub = this.sideMenuService.currentSite.subscribe((siteInfo:any) => {
			if (siteInfo) 
				this.siteInfo = siteInfo;
			
			this.prepareTableColumns();
			this.prepareChartRanges();
			this.prepareChartData();
		});
	}

	ngOnChanges() {
		this.chartData = [];
		this.ranges = [];
		this.tableData = [];
		this.tableConfig.fileName = (this.title || '') + `_${this.selectedPeriod}`;
		this.columnNames = [this.title, this.translateService.instant('fleet_reports.total_number_of_chargers')];
		this.config.hAxis.title = this.title || this.translateService.instant('site.utilization_score');

		this.prepareTableColumns();
		this.prepareChartRanges();
		this.prepareChartData();
	}

	prepareTableColumns() {
		this.columnConfig = [
			{ key: 'serial_number', name: this.translateService.instant('g.serial_number'), type: "link" },
			{ key: 'device_id', name: this.deviceService.translate('devices.truck_id', {sct_device_type: this.deviceType, is_study_device: false}), type: "string" },
			{ key: 'value', name: this.title, type: "number" },
		];

		const ticks = [];
		for (let i = 0; i <= 100; i += 20)
			ticks.push(i);
		this.config.vAxis.ticks = ticks;
	}

	prepareChartData() {
		this.data ||= [];
		if (Array.isArray(this.data)) {
			for (const deviceData of this.data) {
				let value = Math.floor(deviceData[this.toDrawValue] || 0);
				if (value > this.maxValue)
					value = this.maxValue;

				const range = this.ranges.find(r => value >= r.from && value <= r.to);
				if (range)
					range.devices.push(deviceData.mac_address);
			}
		}

		const totalUnits = this.data.length;

		for (const range of this.ranges) {
			const stepPercent = totalUnits ? (range.devices.length / totalUnits) * 100 : 0;
			this.chartData.push([range.label, +(stepPercent).toFixed(2)]);
		}
	}

	prepareChartRanges() {
		const ranges = [];
		for (let i = 0; i < this.maxValue; i = i + 10) {
			let from = i ? i + 1 : 0;
			const isLastRange = i + 10 >= this.maxValue;

			ranges.push({
				devices: [],
				from,
				to: i + 10,
				label: isLastRange ? `${from}% and above` : `${from} - ${i + 10}%`,
			});
		}

		this.ranges = ranges;
	}

	onSelectRange(event: any) {
		if (!event || !('row' in event))
			return;

		this.tableData = [];
		const groupIndex = event.row;
		const selectedGroup = this.ranges[groupIndex] || null;

		if (!selectedGroup)
			return;

		const selectedDevices = selectedGroup.devices;

		for (let macAddress in this.devices) {
			if (!selectedDevices.includes(macAddress))
				continue;

			const deviceData = this.data.find(d => d.mac_address === macAddress) || {};
			const device = this.devices[macAddress] || {};
			const value = deviceData[this.toDrawValue] || 0;
			this.tableData.push({
				serial_number: { value: device.serial_number, link: ['/', this.customerID, this.siteID, device.mac_address, 'performance'] },
				device_id: { value: device.truck_id },
				value: { value: value },
			});
		}

		this.selectedRangeTitle = selectedGroup.label;
		this.tableConfig.csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.siteInfo.customer_name,
			siteName: this.siteInfo.name,
			includeSpacing: false
		});
		this.popupModal.show();
	}

	ngOnDestroy() {
		if (this.popupModal)
			this.popupModal.hide();
	}
}
